<template>
  <div class="cp-page">
    <!-- Animated Background Orbs -->
    <div class="cp-bg-orb cp-bg-orb--1"></div>
    <div class="cp-bg-orb cp-bg-orb--2"></div>

    <!-- Minimal floating top bar -->
    <div class="cp-topbar">
      <router-link class="cp-logo" :to="{name: 'HomePage'}" replace style="position: fixed; top: 16px; left: 20px; z-index: 1050; text-decoration: none;">
        <span class="stella-logo-text" style="font-size: 24px;">✨ stella✦</span>
      </router-link>
      
      <router-link v-if="!isUserLoggedIn" class="login-btn cp-login-btn" :to="{name: 'LoginMobile'}" style="position: fixed; top: 16px; right: 20px; z-index: 1050;">
        Login
      </router-link>
    </div>

    <loading-modal :is-loading="isLoading"></loading-modal>

    <!-- ====== SUBSCRIBER-ONLY RESTRICTED VIEW ====== -->
    <template v-if="isAccessRestricted">
      <section class="cp-hero d-flex align-items-center justify-content-center py-5" style="min-height: 80vh; position: relative; z-index: 2;">
        <div class="container my-auto pt-4">
          <div class="row justify-content-center">
            <div class="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5">
              <div class="cp-content-card p-4 p-md-5 text-center">
                <div class="avatar-glow-wrapper mb-3">
                  <img v-if="!isAvatarMissing" :src="imageURL" class="avatar-glow-img" @error="imageLoadError">
                  <img v-else src="@/assets/images/default_user.png" class="avatar-glow-img">
                  <div class="avatar-badge" style="background: #F3DA6B; color: #111;"><i class="fa fa-lock"></i></div>
                </div>

                <h3 class="text-white font-weight-700 mb-1">{{ inviteeName ? `@${inviteeName}` : 'Private Profile' }}</h3>
                <div class="badge rounded-pill mb-3 px-3 py-1 font-size-12" style="background: rgba(243, 218, 107, 0.15); color: #F3DA6B; border: 1px solid rgba(243, 218, 107, 0.3);">
                  <i class="fa fa-lock me-1"></i> Subscribers Only
                </div>

                <p class="text-white opacity-75 font-size-14 mb-4">
                  This creator's profile, exclusive content, and direct messages are only accessible to active subscribers.
                </p>

                <div v-if="!isUserLoggedIn" class="d-flex align-items-center justify-content-between gap-3 mt-4">
                  <button class="cp-btn-ghost flex-fill py-2" @click="$router.push('/top-creators')">
                    Explore Creators
                  </button>
                  <button class="cp-btn-primary flex-fill py-2" @click="$router.push({ name: 'LoginMobile', params: { urlPrefix: $route.params.urlPrefix }, query: { redirect: $route.fullPath } })">
                    Log In to Access
                  </button>
                </div>
                <div v-else class="d-flex align-items-center justify-content-between gap-3 mt-4">
                  <button class="cp-btn-ghost flex-fill py-2" @click="$router.push('/top-creators')">
                    Explore Creators
                  </button>
                  <button class="cp-btn-primary flex-fill py-2" @click="$router.push({ name: 'CustomerDashboardMobile' })">
                    My Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- ====== STANDARD PROFILE VIEW ====== -->
    <template v-else>
      <section class="cp-hero">
      <!-- Banner image with gradient fade and carousel -->
      <div id="creator-carousel" class="cp-banner carousel slide" data-bs-ride="carousel">
        <div v-if="!wall1ImgURL" class="cp-banner__skeleton"></div>

        <div class="carousel-indicators" style="z-index: 5;" v-if="wall2ImgURL">
          <button aria-current="true"
                  aria-label="Slide 1"
                  data-bs-slide-to="0"
                  data-bs-target="#creator-carousel" type="button"
                  :class="wallItemIndex === 0 ? 'active':''"
                  @click="wallItemIndex = 0"></button>
          <button aria-label="Slide 2"
                  data-bs-slide-to="1"
                  data-bs-target="#creator-carousel"
                  :class="wallItemIndex === 1 ? 'active':''"
                  type="button"
                  @click="wallItemIndex = 1"></button>
        </div>

        <div class="carousel-inner" v-if="wall1ImgURL">
          <div class="carousel-item"
               :class="wallItemIndex === 0 ? 'active':''">
            <img class="cp-banner__img d-block w-100" 
                 :src="wall1ImgURL" 
                 alt="Creator Banner"
                 fetchpriority="high"
                 decoding="async">
          </div>
          <div class="carousel-item" v-if="wall2ImgURL"
               :class="wallItemIndex === 1 ? 'active':''">
            <img class="cp-banner__img d-block w-100" 
                 :src="wall2ImgURL" 
                 alt="Creator Banner Slide 2"
                 loading="lazy"
                 decoding="async">
          </div>
        </div>
        
        <div class="cp-banner__fade"></div>
      </div>

      <!-- Avatar -->
      <div class="cp-avatar-wrap">
        <div class="cp-avatar-ring">
          <img v-if="!isAvatarMissing" :src="imageURL" class="cp-avatar__img" @error="imageLoadError">
          <img v-else src="@/assets/images/default_user.png" class="cp-avatar__img">
        </div>
      </div>

      <!-- Name & Bio -->
      <h1 class="cp-name">{{ inviteeName ? `@${inviteeName}` : '' }}</h1>
      <p class="cp-bio" v-if="bio">{{ bio }}</p>

      <!-- Social links -->
      <div class="cp-socials" v-if="influencerLinks && influencerLinks.length">
        <a v-for="(influencerLink, index) in influencerLinks" :key="index"
           class="cp-social-pill"
           :href="anchorHref(influencerLink)" target="_blank">
          {{ influencerLink.platformName }}
        </a>
      </div>
    </section>

    <!-- ====== PLATFORM LINKS ====== -->
    <section class="cp-section">
      <div class="cp-section-header">
        <h2 class="cp-section-title">Subscriptions</h2>
        <p class="cp-section-sub">Choose a platform to connect</p>
      </div>

      <global-message/>

      <div v-if="isLoading" class="cp-links cp-links--skeleton">
        <div class="cp-link-btn cp-link-btn--skeleton" v-for="i in 2" :key="'skel-' + i">
          <div class="cp-skeleton-text"></div>
          <div class="cp-skeleton-arrow"></div>
        </div>
      </div>
      <div v-else class="cp-links">
        <button v-if="isUserLoggedIn" class="cp-link-btn cp-link-btn--primary"
                @click="$router.replace({name: 'TipPayment', query: {donate: true}})">
          <span class="cp-link-btn__label">💰 Donate</span>
          <span class="cp-link-btn__arrow">→</span>
        </button>
        <button v-for="(influencerPlatform, index) in influencerPlatforms"
                :key="index" @click="checkPlatform(influencerPlatform)"
                class="cp-link-btn" :class="{'cp-link-btn--primary': !isUserLoggedIn && index === 0}">
          <span class="cp-link-btn__label">{{ getButtonLabel(influencerPlatform) }}</span>
          <span class="cp-link-btn__arrow">→</span>
        </button>
      </div>
    </section>

    <!-- ====== BONUS CONTENT ====== -->
    <section class="cp-section" v-if="contents.length > 0">
      <div class="cp-section-header">
        <h2 class="cp-section-title">Bonus Content</h2>
        <p class="cp-section-sub">Exclusive content just for subscribers</p>
      </div>

      <div class="cp-marquee-container">
        <div v-for="rowIndex in Math.ceil(Math.min(contents.length, 12) / 3)" :key="rowIndex" 
             class="cp-marquee-row" 
             :class="(rowIndex % 2 === 0) ? 'cp-marquee-row--rtl' : 'cp-marquee-row--ltr'">
          <div class="cp-marquee-track">
            <!-- Render the chunk twice for infinite loop effect -->
            <template v-for="duplicateIndex in 2">
              <div v-for="(content, idx) in contents.slice((rowIndex - 1) * 3, rowIndex * 3)" :key="`${rowIndex}-${duplicateIndex}-${idx}`" class="cp-marquee-item">
                <div class="cp-content-card mx-auto">
                  <div v-if="!content.cdnUrl" class="cp-content-card__lock">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#E53BB9" stroke-width="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </div>
                  <template v-if="content.cdnUrl">
                    <img v-if="content.type === CONTENT_TYPE_IMAGE" :src="content.cdnUrl" class="cp-content-card__media">
                    <video v-else-if="content.type === CONTENT_TYPE_VIDEO" :src="content.cdnUrl" controls controlsList="nodownload" oncontextmenu="return false;" class="cp-content-card__media"></video>
                  </template>
                  <div class="cp-content-card__body">
                    <span class="cp-content-card__title" :title="content.description">{{ content.description }}</span>
                    <button v-if="!content.cdnUrl" class="cp-unlock-btn" @click="subscribe(content)">
                      Subscribe to unlock
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </section>

    <!-- ====== OFFERINGS ====== -->
    <section class="cp-section">
      <div class="cp-section-header">
        <h2 class="cp-section-title">Offerings</h2>
        <p class="cp-section-sub">Choose from my available services or send your custom request</p>
      </div>

      <div v-if="isLoading" class="d-flex justify-content-center py-4">
        <div class="spinner-border" role="status" style="color: #E53BB9;"></div>
      </div>
      <div v-else class="cp-requests">
        <button v-for="(customRequestOption, index) in customRequestOptions" :key="index"
                class="cp-request-row" @click="submitCustomRequest(index)">
          <span class="cp-request-row__title">{{ customRequestOption.title }}</span>
          <span class="cp-request-row__price">${{ customRequestOption.price }}</span>
        </button>
        <button class="cp-request-row cp-request-row--custom" @click="submitCustomRequest(null)">
          <span class="cp-request-row__title">Custom</span>
          <span class="cp-request-row__price">${{ minRequired }} min</span>
        </button>
      </div>
    </section>

    <!-- ====== CTA ====== -->
    <section class="cp-section">
      <div class="cp-cta-card">
        <h3 class="cp-cta-card__title">Get all-access to exclusive content!</h3>
        <p class="cp-cta-card__sub">Subscribe for exclusive content, custom requests, and more.</p>
        <button class="cp-cta-card__btn" @click="$refs.platform.show()">Full Access →</button>
      </div>
    </section>

    <!-- Footer -->
    <footer class="cp-footer">
      <span class="stella-logo-text" style="font-size: 24px; text-decoration: none; cursor: pointer;">✨ stella✦</span>
      <span class="cp-footer__text">Powered by Stella</span>
    </footer>
    </template>

    <!-- Modals (unchanged) -->
    <model-platform ref="platform"
                    :url-prefix="$route.params.urlPrefix"
                    :invitee-name="inviteeName"
                    :image-u-r-l="imageURL"
                    :platforms="influencerPlatforms"
                    @platformSelected="platformSelected()"
                    @platformNotRegistered="goToPlatform"/>
    <!-- Model to display subscription  -->
    <model-subscription ref="subscription" v-if="getInvitee"
                        :invitee="getInvitee"
                        :image-u-r-l="imageURL"/>

    <!-- Custom request -->
    <model-customer-custom-request-submit
      ref="customRequestConfirm"
      @savedCustomRequest="(customRequest) => {
        $refs.customRequestDone.show(customRequest);
     }"
      @submittedCustomRequestOption="(customRequest) => {
        $refs.customRequestOption.show(customRequest, getUser.id);
      }"/>

    <model-customer-custom-request-option-info
      ref="customRequestOption"
      @savedCustomRequestOption="(customRequest) => {
        if(customRequest.status === CR_STATUS_ACCEPTED){
               $refs.customRequestAccepted.show(customRequest);
            } else if(customRequest.status === CR_STATUS_READY) {
               $refs.customRequestDone.show(customRequest, true);
            }
      }"
      @back="() => {
         $refs.customRequestConfirm.simpleShow();
      }"
    />

    <model-customer-custom-request-accepted ref="customRequestAccepted"/>

    <model-customer-custom-request-done
      ref="customRequestDone" @doneCustomRequest="() => {
      }"/>

  </div>
</template>

<script>

import {SERVICE_CONTENT, SERVICE_IDENTIY, SERVICE_SUBSCRIPTION} from "@/properties/micro-service";
import {geDecodedToken, getTokenExp, isRole, isTokenExpired} from "@/common/http-common";
import {
  ACCESS_TOKEN,
  PLATFORM_INSTAGRAM,
  PLATFORM_SNAPCHAT, PLATFORM_STELLA,
  PLATFORM_TELEGRAM,
  PLATFORM_TIKTOK
} from "@/properties/close-friend";
import {
  CONTENT_TYPE_APPLICATION,
  CONTENT_TYPE_IMAGE,
  CONTENT_TYPE_URL,
  CONTENT_TYPE_VIDEO
} from "@/properties/content-type";
import {customerAccountMixin} from "@/mixin/customer-account/customer-account-mixin";
import {mapActions, mapGetters} from "vuex";
import {isUserActive} from "@/common/comm-service";
import GlobalMessage from "@/components/module/shared/GlobalMessage";
import {CR_STATUS_ACCEPTED, CR_STATUS_READY} from "@/properties/custom-request-status";
import titleMixin from "../../../../mixin/title-mixin";
import {clone} from '@/common/Utils';
import {AXIOS, AXIOSunsecure} from "@/common/http-common";
import {isCreatorPrivate} from "@/common/creator-access";
import ModelPlatform from "@/components/module/customer/account/ModelPlatform";
import ModelSubscription from "@/components/module/customer/account/ModelSubscription";
import ModelCustomerCustomRequestConfirm
  from "@/components/module/customer/custom-request/ModelCustomerCustomRequestConfirm";
import ModelCustomerCustomRequestDone from "@/components/module/customer/custom-request/ModelCustomerCustomRequestDone";
import ModelCustomerCustomRequestSubmit
  from "@/components/module/customer/custom-request/ModelCustomerCustomRequestSubmit";
import ModelCustomerCustomRequestOptionInfo
  from "@/components/module/customer/custom-request/ModelCustomerCustomRequestOptionInfo";
import ModelCustomerCustomRequestAccepted
  from "@/components/module/customer/custom-request/ModelCustomerCustomRequestAccepted";
import LoadingModal from "@/components/module/shared/LoadingModal";

export default {
  name: "CreatorPage",
  title: 'Creator',
  props: ['query'],
  components: {
    GlobalMessage,
    LoadingModal,
    ModelPlatform,
    ModelSubscription,
    ModelCustomerCustomRequestConfirm,
    ModelCustomerCustomRequestDone,
    ModelCustomerCustomRequestSubmit,
    ModelCustomerCustomRequestOptionInfo,
    ModelCustomerCustomRequestAccepted
  },
  mixins: [customerAccountMixin, titleMixin],
  data() {
    return {
      inviteeName: '',
      bio: '',
      influencerPlatforms: [],
      customRequestOptions: [],
      minRequired: 0,
      notificaiton: false,
      contents: [],
      influencerLinks: [],
      selectedContent: null,
      pageNumber: 0,
      noOfItems: 12,
      isUserLoggedIn: false,
      isAccessRestricted: false,
      isLoading: true,

      wallImgURLs: [],
      CONTENT_TYPE_IMAGE,
      CONTENT_TYPE_VIDEO,
      crItemIndex: 0,
      wallItemIndex: 0,
      first: true,

      CR_STATUS_ACCEPTED,
      CR_STATUS_READY,

    }
  },
  mounted() {
    document.body.style.backgroundColor = '';
  },
  created() {
    this.clearAllStore();
    this.setCustomRequestInfluencer(null);
    this.setCustomRequestIndex(null);
    let tokenExp = getTokenExp(ACCESS_TOKEN);
    if (tokenExp && !isTokenExpired(ACCESS_TOKEN)) {
      this.isUserLoggedIn = true;
      if (!isRole('INFLUENCER')) {
        if (!this.getUser) {
          let customerId = geDecodedToken(ACCESS_TOKEN).id;
          this.getData(`${SERVICE_IDENTIY}/api/v1/customers/${customerId}`, (customer) => {
            this.setUser(customer);
          });
        }
      }
    }

    // Fire avatar and invitee lookups in parallel (both use urlSuffix, no dependency)
    this.loadAvatarImg();
    this.loadInviteeData();
  },
  computed: {
    wall1MobileImgURL() {
      let mobileURL = this.wallImgURLs.find(e => e[0].includes('wall1_mobile'));
      if (!mobileURL) {
        mobileURL = this.wallImgURLs.find(e => e[0].includes('wall1')); //Get default
      }
      return mobileURL? mobileURL[0]:null;
    },
    wall1WebImgURL() {
      let webURL = this.wallImgURLs.find(e => e[0].includes('wall1_web'));
      if (!webURL) {
        webURL = this.wallImgURLs.find(e => e[0].includes('wall1')); //Get default
      }
      return webURL? webURL[0]:null;
    },

    wall1ImgURL() {
      if (this.isMobile()) {
        return !this.wall1MobileImgURL ? this.wall2MobileImgURL : this.wall1MobileImgURL;
      } else {
        return !this.wall1WebImgURL ? this.wall2WebImgURL : this.wall1WebImgURL;
      }
    },

    wall2MobileImgURL() {
      let mobileURL = this.wallImgURLs.find(e => e[0].includes('wall2_mobile'));
      if (!mobileURL) {
        mobileURL = this.wallImgURLs.find(e => e[0].includes('wall2')); //Get default
      }
      return mobileURL? mobileURL[0]:null;
    },
    wall2WebImgURL() {
      let webURL = this.wallImgURLs.find(e => e[0].includes('wall2_web'));
      if (!webURL) {
        webURL = this.wallImgURLs.find(e => e[0].includes('wall2')); //Get default
      }
      return webURL? webURL[0]:null;
    },
    wall2ImgURL() {
      if (this.isMobile()) {
        return this.wall1MobileImgURL ? this.wall2MobileImgURL : '';
      } else {
        return this.wall1WebImgURL ? this.wall2WebImgURL : '';
      }
    }
  },
  methods: {
    ...mapActions(['setMessage', 'setUser', 'setInvitee',
      'setPlatform', 'clearAllStore', 'setCustomRequestInfluencer', 'setCustomRequestIndex']),
    ...mapGetters(['getCustomRequestInfluencer']),
    anchorHref(influencerLink) {
      if (influencerLink.url.toLowerCase().includes('http')) {
        return influencerLink.url;
      } else {
        return 'http://' + influencerLink.url;
      }
    },
    getButtonColor(influencerPlatform) {
      if (this.isInstagramPlatform(influencerPlatform)) {
        return 'btn btn-success';
      } else if (this.isTelegramPlatform(influencerPlatform)) {
        return 'btn btn-blue'
      } else if (this.isOtherPlatform(influencerPlatform)) {
        return 'btn btn-success';
      }
      return 'btn btn-black'
    },
    getButtonLabel(influencerPlatform) {
      if (this.isInstagramPlatform(influencerPlatform)) {
        return 'Instagram Close Friends';
      } else if (this.isSnapchatPlatform(influencerPlatform)) {
        return 'Private Snapchat';
      } else if(influencerPlatform.isMessage) {
        return 'Send a message';
      } else if (this.isStellaPlatform(influencerPlatform)) {
        return 'All-Access';
      }
      return `Exclusive ${influencerPlatform.platform.name}`;
    },
    isInstagramPlatform(influencerPlatform) {
      if (influencerPlatform.platform.name.includes(PLATFORM_INSTAGRAM)) {
        return true;
      }
      return false;
    },
    isSnapchatPlatform(influencerPlatform) {
      if (influencerPlatform.platform.name.includes(PLATFORM_SNAPCHAT)) {
        return true;
      }
      return false;
    },
    isStellaPlatform(influencerPlatform) {
      if (influencerPlatform.platform.name.includes(PLATFORM_STELLA)) {
        return true;
      }
      return false;
    },
    isTelegramPlatform(influencerPlatform) {
      if (influencerPlatform.platform.name.includes(PLATFORM_TELEGRAM)) {
        return true;
      }
      return false;
    },
    isOtherPlatform(influencerPlatform) {
      let platform = influencerPlatform.platform.name;
      if (platform === PLATFORM_SNAPCHAT
        || platform === PLATFORM_INSTAGRAM
        || platform === PLATFORM_TIKTOK
        || platform === PLATFORM_TELEGRAM) {
        return false;
      }
      return true;
    },

    checkPlatform(influencerPlatform) {
      let customer = this.getUser;
      //This means it is logged in user
      if (customer) {
        let custPlatform = customer.customerPlatforms
          .find(customerPlatform => customerPlatform.platform.name === influencerPlatform.platform.name);
        //Check if the customer has registered with this platform
        if (custPlatform) {
          this.setPlatform(influencerPlatform);
          this.$router.replace({name: 'CustomerRouter'});
        } else {
          this.goToPlatform(influencerPlatform);
        }
      } else {
        this.goToPlatform(influencerPlatform);
      }
    },
    goToPlatform(influencerPlatform) {
      if (influencerPlatform) {
        console.log('influencerPlatform.platform', influencerPlatform.platform);
        this.setPlatform(influencerPlatform);
      }
      if (influencerPlatform && this.isInstagramPlatform(influencerPlatform)) {
        this.$router.push({path: `/c/${this.$route.params.urlPrefix}`});
      } else if (influencerPlatform && this.isSnapchatPlatform(influencerPlatform)) {
        this.$router.push({path: `/snapchat/${this.$route.params.urlPrefix}`});
      } else if (influencerPlatform && this.isOtherPlatform(influencerPlatform)) {
        this.$router.push({path: `/platform/${this.$route.params.urlPrefix}`});
      } else if (influencerPlatform) {
        this.$router.push({path: `/tiktok/${this.$route.params.urlPrefix}`});
      }
    },
    /**
     * Fetches a URL using AXIOSunsecure and returns a Promise.
     * This bypasses the mixin's getUnSecureData() to avoid isLoading race conditions
     * and to enable Promise.all parallelization.
     */
    fetchUnsecure(url) {
      return AXIOSunsecure().get(url).then(response => response.data);
    },

    verifySubscriberAccess(invitee) {
      let tokenExp = getTokenExp(ACCESS_TOKEN);
      if (!tokenExp || isTokenExpired(ACCESS_TOKEN)) {
        return Promise.resolve(false);
      }

      let decoded = null;
      try {
        decoded = geDecodedToken(ACCESS_TOKEN);
      } catch (e) {
        return Promise.resolve(false);
      }

      if (!decoded || !decoded.id) {
        return Promise.resolve(false);
      }

      // Check if logged-in user is the creator themself
      if (isRole('INFLUENCER')) {
        const isSelf = decoded.id === invitee.id || (this.getUser && this.getUser.id === invitee.id);
        return Promise.resolve(isSelf);
      }

      // Check if logged-in customer has an active subscription for this creator
      const customerId = decoded.id;
      const url = `${SERVICE_SUBSCRIPTION}/api/v1/customers/${customerId}/subscriptions/influencer/${invitee.id}/active`;
      return AXIOS().get(url)
        .then(response => {
          const activeSubs = response.data;
          return Array.isArray(activeSubs) && activeSubs.length > 0;
        })
        .catch(err => {
          console.warn('Could not verify subscriber access:', err);
          return false;
        });
    },

    loadSubRequests(invitee) {
      // Step 2: Fire ALL 4 sub-requests in parallel using Promise.allSettled
      const platformPromise = this.fetchUnsecure(
        `${SERVICE_SUBSCRIPTION}/api/v1/influencers/${invitee.id}/platform`
      );
      const customRequestPromise = this.fetchUnsecure(
        `${SERVICE_CONTENT}/api/v1/public/influencers/${invitee.id}/custom-request-options/`
      );
      const wallPromise = this.fetchUnsecure(
        `${SERVICE_CONTENT}/api/v1/public/influencers/${invitee.id}/wall`
      );
      const contentPromise = this.fetchUnsecure(
        `${SERVICE_CONTENT}/api/v1/public/influencers/${invitee.id}/content/page?pageNumber=${this.pageNumber}&numberOfItems=${this.noOfItems}`
      );

      return Promise.allSettled([platformPromise, customRequestPromise, wallPromise, contentPromise])
        .then(([platformResult, customRequestResult, wallResult, contentResult]) => {

          // Process platforms
          if (platformResult.status === 'fulfilled') {
            this.influencerPlatforms = platformResult.value;
            let index = this.influencerPlatforms
              .findIndex((infPlatform) => infPlatform.platform.name === PLATFORM_STELLA);
            if (index !== -1 && index !== 0) {
              let stellaPlatform = this.influencerPlatforms[index];
              this.influencerPlatforms.splice(index, 1);
              this.influencerPlatforms.unshift(stellaPlatform);

              //Clone stella platform create clone and mark as message platform
              let sendMessage = clone(stellaPlatform);
              sendMessage.isMessage = true;
              this.influencerPlatforms.push(sendMessage);
            }
          }

          // Process custom request options
          if (customRequestResult.status === 'fulfilled') {
            this.customRequestOptions = customRequestResult.value;
          }

          // Process wall images
          if (wallResult.status === 'fulfilled') {
            this.wallImgURLs = wallResult.value;
            this.$nextTick(() => {
              $('.carousel').carousel();
              $('#creator-carousel').on('slide.bs.carousel', (val) => {
                this.wallItemIndex = val.to;
              });
            });
          }

          // Process bonus content
          if (contentResult.status === 'fulfilled') {
            let page = contentResult.value;
            if (!page.empty) {
              this.contents.push(...page.content);
              this.$nextTick(() => {
                $('.carousel').carousel();
                $('#custom-request-carousel').on('slide.bs.carousel', (val) => {
                  this.crItemIndex = val.to;
                });
              });
            }
          }

          // All requests settled — hide loading
          this.isLoading = false;
        });
    },

    loadInviteeData() {
      this.isLoading = true;
      let urlPrefix = this.$route.params.urlPrefix;

      // Step 1: Fetch invitee data (needed to get invitee.id for subsequent calls)
      this.fetchUnsecure(`${SERVICE_IDENTIY}/api/v1/influencers/urlSuffix/${urlPrefix}`)
        .then(invitee => {
          if (!isUserActive(invitee)) {
            this.$router.replace({name: 'DisabledAccount'});
            return;
          }

          this.inviteeName = invitee.name;
          this.bio = invitee.bio;
          this.minRequired = invitee.customRequestMinPrice;
          this.influencerLinks = invitee.influencerLinks;
          this.setInvitee(invitee);
          this.setTitle();

          // Gate private creators for subscriber-only access
          const isPrivate = isCreatorPrivate(urlPrefix) || (invitee && (isCreatorPrivate(invitee.urlSuffix) || isCreatorPrivate(invitee.name)));
          if (isPrivate) {
            return this.verifySubscriberAccess(invitee).then(hasAccess => {
              if (!hasAccess) {
                this.isAccessRestricted = true;
                this.isLoading = false;
                return;
              }
              this.isAccessRestricted = false;
              return this.loadSubRequests(invitee);
            });
          } else {
            this.isAccessRestricted = false;
            return this.loadSubRequests(invitee);
          }
        })
        .catch(e => {
          this.isLoading = false;
          this.catchError(e, true);
        });
    },
    subscribe(content) {
      this.$refs.platform.show();
      this.selectedContent = content;
    },
    platformSelected() {
      this.$refs.subscription.show();
    },
    submitCustomRequest(index) {
      this.setCustomRequestInfluencer(this.getInvitee);
      this.setCustomRequestIndex(index);

      if (!this.isUserLoggedIn) {
        if (this.$refs.platform) {
          this.$refs.platform.show('Before submitting a custom request');
        }
        return;
      }
      let influencer = this.getInvitee;
      if (this.$refs.customRequestConfirm) {
        this.$refs.customRequestConfirm.show(influencer.id, this.getUser.id, influencer.customRequestMinPrice);
        if (index === null) {
          this.$refs.customRequestConfirm.chosenCustomRequest();
        } else {
          this.$refs.customRequestConfirm.chosenCustomRequestOption(index);
        }
      }
    }

  }
}
</script>

<style scoped>
/* ====== Creator Page — Full Redesign ====== */

/* Page wrapper */
.cp-page {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

/* Background animated orbs using Stella brand colors */
.cp-bg-orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;
}
.cp-bg-orb--1 {
  width: 500px; height: 500px;
  background: #E53BB9;
  top: -150px; left: -100px;
  animation: cp-orbFloat1 12s ease-in-out infinite;
}
.cp-bg-orb--2 {
  width: 400px; height: 400px;
  background: #F3DA6B;
  bottom: -100px; right: -80px;
  animation: cp-orbFloat2 10s ease-in-out infinite;
}
@keyframes cp-orbFloat1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(60px, 40px); }
}
@keyframes cp-orbFloat2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-50px, -30px); }
}

/* ====== TOP BAR ====== */
.cp-topbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;
  pointer-events: none; /* Let clicks pass through empty space */
}

/* Floating logo */
.cp-logo {
  pointer-events: auto;
}
.cp-logo img {
  height: 26px;
}

/* Login button */
.cp-login-btn {
  pointer-events: auto;
}

/* ====== HERO ====== */
.cp-hero {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 20px;
  z-index: 1;
}

/* Banner */
.cp-banner {
  width: 100%;
  height: 300px;
  min-height: 300px;
  max-height: 300px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(135deg, #18181f 0%, #111114 100%);
}
.cp-banner__skeleton {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 300px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.02) 25%, rgba(255, 255, 255, 0.06) 50%, rgba(255, 255, 255, 0.02) 75%);
  background-size: 200% 100%;
  animation: cp-shimmer 2s infinite;
}
@keyframes cp-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.cp-banner .carousel-inner,
.cp-banner .carousel-item {
  height: 100%;
}
.cp-banner__img {
  width: 100%;
  height: 300px;
  object-fit: cover;
  display: block;
}
.cp-banner__fade {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 60%;
  background: linear-gradient(to top, #000 0%, transparent 100%);
  pointer-events: none;
}

/* Avatar with animated rainbow ring */
.cp-avatar-wrap {
  margin-top: -55px;
  z-index: 10;
  position: relative;
  width: 110px;
  height: 110px;
  flex-shrink: 0;
}
.cp-avatar-ring {
  width: 110px; height: 110px;
  border-radius: 50%;
  padding: 4px;
  background: conic-gradient(from 0deg, #F3DA6B, #E53BB9, #70D5C2, #FFDB7E, #E8AFED, #F3DA6B);
  animation: cp-ringRotate 4s linear infinite;
}
@keyframes cp-ringRotate {
  to { filter: hue-rotate(360deg); }
}
.cp-avatar__img {
  width: 100%; height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #000;
}

/* Name */
.cp-name {
  margin-top: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(90deg, #F3DA6B, #E53BB9);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-align: center;
  z-index: 1;
}

/* Bio */
.cp-bio {
  color: #9d9d9d;
  font-size: 15px;
  text-align: center;
  max-width: 420px;
  margin: 4px auto 0;
  line-height: 1.5;
  z-index: 1;
}

/* Social pills */
.cp-socials {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  z-index: 1;
}
.cp-social-pill {
  padding: 6px 16px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 600;
  text-transform: capitalize;
  color: #fff;
  text-decoration: none;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  transition: all 0.25s ease;
  letter-spacing: 0.3px;
}
.cp-social-pill:hover {
  background: linear-gradient(90deg, rgba(243,218,107,0.15), rgba(229,59,185,0.15));
  border-color: rgba(229,59,185,0.4);
  color: #fff;
  transform: translateY(-2px);
}

/* ====== SECTIONS ====== */
.cp-section {
  position: relative;
  z-index: 1;
  max-width: 1000px;
  margin: 0 auto 30px;
  padding: 30px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
}
@media (max-width: 991px) {
  .cp-section {
    max-width: 600px;
    margin: 0 16px 24px;
    padding: 24px 20px;
  }
}
.cp-section-header {
  margin-bottom: 16px;
}
.cp-section-title {
  font-family: 'Poppins', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 0;
}
.cp-section-sub {
  color: #9d9d9d;
  font-size: 14px;
  margin: 4px 0 0;
}

/* ====== LINK BUTTONS ====== */
.cp-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
@media (min-width: 768px) {
  .cp-links {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;
  }
}
.cp-link-btn {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 14px;
  background: rgba(255,255,255,0.03);
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
}

/* Give each subsequent link a different accent color */
.cp-link-btn:nth-child(1n) { border-left: 4px solid #E53BB9; }
.cp-link-btn:nth-child(2n) { border-left: 4px solid #F3DA6B; }
.cp-link-btn:nth-child(3n) { border-left: 4px solid #70D5C2; }
.cp-link-btn:nth-child(4n) { border-left: 4px solid #FFDB7E; }
.cp-link-btn:nth-child(5n) { border-left: 4px solid #AFE1D8; }

/* Skeleton state for link buttons */
.cp-link-btn--skeleton {
  pointer-events: none;
  background: rgba(255, 255, 255, 0.03) !important;
  border-left: 4px solid rgba(255, 255, 255, 0.12) !important;
  min-height: 58px;
}
.cp-skeleton-text {
  width: 140px;
  height: 16px;
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.03) 25%, rgba(255, 255, 255, 0.08) 50%, rgba(255, 255, 255, 0.03) 75%);
  background-size: 200% 100%;
  animation: cp-shimmer 1.8s infinite;
}
.cp-skeleton-arrow {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
}

.cp-link-btn:not(.cp-link-btn--primary):hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateX(6px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);
}

/* Primary Button Styling (Donate or First Item) */
.cp-link-btn--primary {
  background: linear-gradient(90deg, #F3DA6B, #E53BB9, #70D5C2, #F3DA6B) !important;
  background-size: 300% 100% !important;
  animation: gradient-shift 6s linear infinite !important;
  color: #222023 !important;
  border: none !important;
  font-weight: 700 !important;
}
.cp-link-btn--primary .cp-link-btn__arrow {
  color: #222023 !important;
}
.cp-link-btn--primary:hover {
  background: linear-gradient(90deg, #F3DA6B, #E53BB9, #70D5C2, #F3DA6B) !important;
  background-size: 300% 100% !important;
  animation: gradient-shift 6s linear infinite !important;
  transform: translateY(-4px) !important;
  box-shadow: 0 10px 25px rgba(229,59,185,0.4) !important;
}
.cp-link-btn__label { z-index: 1; }
.cp-link-btn__arrow {
  font-size: 20px;
  color: #E53BB9;
  z-index: 1;
  transition: transform 0.2s ease;
}
.cp-link-btn:hover .cp-link-btn__arrow {
  transform: translateX(4px);
}

/* ====== BONUS CONTENT MARQUEE ====== */
.cp-marquee-container {
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.cp-marquee-row {
  display: flex;
  width: 100%;
  overflow: hidden;
}
.cp-marquee-track {
  display: flex;
  gap: 20px;
  width: max-content;
}
/* Pause on hover */
.cp-marquee-row:hover .cp-marquee-track {
  animation-play-state: paused;
}
.cp-marquee-item {
  width: 280px; /* Fixed width for cards */
  flex-shrink: 0;
}
/* LTR Animation (Row 1, Row 3) */
.cp-marquee-row--ltr .cp-marquee-track {
  animation: scroll-ltr 25s linear infinite;
}
@keyframes scroll-ltr {
  0% { transform: translateX(calc(-50% - 10px)); }
  100% { transform: translateX(0); }
}
/* RTL Animation (Row 2, Row 4) */
.cp-marquee-row--rtl .cp-marquee-track {
  animation: scroll-rtl 25s linear infinite;
}
@keyframes scroll-rtl {
  0% { transform: translateX(0); }
  100% { transform: translateX(calc(-50% - 10px)); }
}
.cp-content-card {
  border-radius: 14px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  overflow: hidden;
  transition: all 0.3s ease;
}
.cp-content-card:hover {
  border-color: rgba(229,59,185,0.25);
  box-shadow: 0 6px 20px rgba(229,59,185,0.12);
}
.cp-content-card__lock {
  display: flex;
  justify-content: center;
  padding: 24px 0 8px;
}
.cp-content-card__media {
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: 14px 14px 0 0;
}
.cp-content-card__body {
  padding: 16px 20px;
}
.cp-content-card__title {
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 48px; /* Ensures short text aligns perfectly with long text cards */
  margin-bottom: 12px;
}
.cp-unlock-btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 15px;
  color: #222023;
  background: linear-gradient(90deg, #F3DA6B, #E53BB9);
  transition: all 0.25s ease;
}
.cp-unlock-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(229,59,185,0.35);
}

/* ====== CUSTOM REQUESTS ====== */
.cp-requests {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
@media (min-width: 768px) {
  .cp-requests {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }
}
.cp-request-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  background: rgba(255,255,255,0.03);
  cursor: pointer;
  transition: all 0.25s ease;
  border-left: 3px solid transparent;
}
.cp-request-row:not(.cp-request-row--custom):hover {
  background: rgba(255,255,255,0.06);
  border-left-color: #E53BB9;
  transform: translateX(6px);
}
.cp-request-row__title {
  color: #fff;
  font-size: 15px;
  font-weight: 500;
}
.cp-request-row__price {
  font-size: 16px;
  font-weight: 700;
  background: linear-gradient(90deg, #F3DA6B, #E53BB9);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.cp-request-row--custom {
  background: linear-gradient(90deg, #F3DA6B, #E53BB9, #70D5C2, #F3DA6B) !important;
  background-size: 300% 100% !important;
  animation: gradient-shift 6s linear infinite !important;
  border: none !important;
  border-left: none !important;
}
.cp-request-row--custom .cp-request-row__title {
  color: #222023 !important;
  font-weight: 700;
}
.cp-request-row--custom .cp-request-row__price {
  background: none !important;
  -webkit-text-fill-color: #222023 !important;
  color: #222023 !important;
}
.cp-request-row--custom:hover {
  background: linear-gradient(90deg, #F3DA6B, #E53BB9, #70D5C2, #F3DA6B) !important;
  background-size: 300% 100% !important;
  animation: gradient-shift 6s linear infinite !important;
  border: none !important;
  border-left: none !important;
  transform: translateY(-3px) scale(1.02) !important;
  box-shadow: 0 10px 30px rgba(229,59,185,0.45) !important;
}
.cp-request-row--custom:hover .cp-request-row__title,
.cp-request-row--custom:hover .cp-request-row__price {
  color: #222023 !important;
  -webkit-text-fill-color: #222023 !important;
}
@keyframes gradient-shift {
  0% { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
}

/* ====== CTA CARD ====== */
.cp-cta-card {
  position: relative;
  padding: 32px 28px;
  border-radius: 18px;
  overflow: hidden;
  text-align: center;
  background: linear-gradient(135deg, rgba(229,59,185,0.1), rgba(243,218,107,0.05));
  border: 1px solid rgba(229,59,185,0.2);
}
.cp-cta-card__title {
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  margin: 0 0 8px;
}
.cp-cta-card__sub {
  color: #9d9d9d;
  font-size: 14px;
  margin: 0 0 20px;
}
.cp-cta-card__btn {
  padding: 12px 40px;
  border: none;
  border-radius: 50px;
  font-weight: 700;
  font-size: 16px;
  color: #222023;
  background: linear-gradient(90deg, #F3DA6B, #E53BB9, #70D5C2, #F3DA6B);
  background-size: 300% 100%;
  animation: gradient-shift 6s linear infinite;
  cursor: pointer;
  transition: all 0.25s ease;
}
.cp-cta-card__btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(229,59,185,0.35);
}

/* ====== FOOTER ====== */
.cp-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0 30px;
  position: relative;
  z-index: 1;
}
.cp-footer__logo {
  height: 24px;
  opacity: 0.5;
}
.cp-footer__text {
  color: #9d9d9d;
  font-size: 12px;
}
</style>
